import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { useTheme } from '../lib/theme-context';
import { Button } from './ui/button';
import {
  LayoutDashboard, PenSquare, Users, Calendar, FileText,
  LogOut, Sun, Moon, BarChart3, UserPlus, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { useState } from 'react';
import UserManagement from './UserManagement';

const allNavItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', roles: ['superadmin', 'user'] },
  { to: '/compose', icon: PenSquare, label: 'Compose', roles: ['superadmin', 'user'] },
  { to: '/accounts', icon: Users, label: 'Accounts', roles: ['superadmin'] },
  { to: '/schedules', icon: Calendar, label: 'Schedules', roles: ['superadmin', 'user'] },
  { to: '/logs', icon: FileText, label: 'Logs', roles: ['superadmin', 'user'] },
  { to: '/analytics', icon: BarChart3, label: 'Analytics', roles: ['superadmin', 'user'] },
];

export default function Sidebar() {
  const { logout, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [showUserMgmt, setShowUserMgmt] = useState(false);

  const isSuperAdmin = user?.role === 'superadmin';
  const navItems = allNavItems.filter(item => item.roles.includes(user?.role || 'user'));

  const handleLogout = () => {
    logout();
    toast.success('Logged out');
    navigate('/login');
  };

  return (
    <>
      <aside
        data-testid="sidebar"
        className="fixed left-0 top-0 h-screen w-64 bg-card border-r border-border flex flex-col z-40"
      >
        {/* Brand */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Mackertich" className="w-9 h-9 object-contain rounded" />
            <div>
              <h1
                className="text-lg font-black tracking-tight leading-none"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                Mackertich Mail
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5">Email Scheduler</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1" data-testid="sidebar-nav">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              data-testid={`nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm transition-all duration-200 ${
                  isActive
                    ? 'bg-indigo-600/10 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-400 font-medium'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}

          {/* User Management - superadmin only */}
          {isSuperAdmin && (
            <button
              onClick={() => setShowUserMgmt(true)}
              data-testid="nav-users"
              className="flex items-center gap-3 px-4 py-2.5 rounded-md text-sm transition-all duration-200 w-full text-left text-muted-foreground hover:bg-secondary hover:text-foreground"
            >
              <UserPlus className="w-4 h-4" />
              Manage Users
            </button>
          )}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-border space-y-3">
          <div className="flex items-center justify-between px-2">
            <div className="flex items-center gap-1.5 min-w-0">
              {isSuperAdmin && <Shield className="w-3 h-3 text-amber-500 shrink-0" />}
              <span className="text-xs text-muted-foreground truncate" data-testid="user-display">
                {user?.username || 'User'}
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground shrink-0">
                {isSuperAdmin ? 'Admin' : 'User'}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              data-testid="theme-toggle"
              onClick={toggleTheme}
              className="h-8 w-8 p-0"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
          </div>
          <Button
            variant="ghost"
            data-testid="logout-button"
            onClick={handleLogout}
            className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </Button>
        </div>
      </aside>

      {/* User Management Modal */}
      {showUserMgmt && <UserManagement onClose={() => setShowUserMgmt(false)} />}
    </>
  );
}
